import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import { authApi } from '../../services/api';
import {
  LayoutDashboard, History, Settings2,
  Users, UserCog, LogOut, ChevronDown, X, Building2
, Lock, Eye, EyeOff } from 'lucide-react';


function ChangePasswordModal({ onClose }) {
  const [current, setCurrent]     = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showCur, setShowCur]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw !== confirm) { setError('New passwords do not match'); return; }
    if (newPw.length < 10) { setError('Password must be at least 10 characters'); return; }
    setSaving(true); setError('');
    try {
      await authApi.changePassword(current, newPw);
      toast.success('Password changed successfully');
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--navy-border)',
                    borderRadius: 12, padding: 28, width: 380, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Lock size={16} style={{ color: 'var(--blue)' }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Change Password</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
                                             color: 'var(--text-dim)', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { label: 'Current password', val: current, set: setCurrent, show: showCur, toggle: () => setShowCur(v=>!v) },
            { label: 'New password',     val: newPw,   set: setNewPw,   show: showNew, toggle: () => setShowNew(v=>!v) },
            { label: 'Confirm new password', val: confirm, set: setConfirm, show: showNew, toggle: () => setShowNew(v=>!v) },
          ].map(({ label, val, set, show, toggle }) => (
            <div key={label}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)',
                               textTransform: 'uppercase', letterSpacing: '0.06em',
                               display: 'block', marginBottom: 6 }}>{label}</label>
              <div style={{ position: 'relative' }}>
                <input type={show ? 'text' : 'password'} value={val}
                       onChange={e => set(e.target.value)}
                       className="input" style={{ paddingRight: 40 }}
                       autoComplete="new-password" required />
                <button type="button" onClick={toggle}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                           background: 'none', border: 'none', cursor: 'pointer',
                           color: 'var(--text-dim)', padding: 0 }}>
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          ))}
          {error && (
            <div style={{ fontSize: 12, color: 'var(--red)', padding: '8px 12px',
                          background: 'rgba(220,38,38,0.08)', borderRadius: 6 }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="button" onClick={onClose} className="btn btn-ghost" style={{ flex: 1 }}>Cancel</button>
            <button type="submit" disabled={saving} className="btn btn-primary" style={{ flex: 1 }}>
              {saving ? 'Saving…' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, tenants, activeTenantId, activeTenantName, switchTenant, clearTenant } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const handleLogout  = () => { logout(); navigate('/login'); };
  const initials      = (user?.full_name || user?.email || '?')[0].toUpperCase();
  const isAdmin       = user?.is_admin;

  const handleSwitch = (tenant) => {
    switchTenant(tenant.id, tenant.name);
    setSwitcherOpen(false);
    navigate('/');
  };

  const handleClear = () => {
    clearTenant();
    setSwitcherOpen(false);
    navigate('/');
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h1>KSI<span>→</span>SYNC</h1>
          <p>Sync Platform</p>
        </div>

        {/* Tenant switcher — global admin only */}
        {isAdmin && tenants.length > 0 && (
          <div style={{ padding: '0 12px 12px', position: 'relative' }}>
            <button
              onClick={() => setSwitcherOpen(o => !o)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', padding: '8px 12px',
                borderRadius: 8, border: '1px solid var(--navy-border)',
                background: activeTenantId ? 'rgba(37,99,235,0.12)' : 'var(--navy)',
                cursor: 'pointer', gap: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <Building2 size={13} style={{ color: activeTenantId ? 'var(--blue)' : 'var(--text-dim)', flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 500, color: activeTenantId ? 'var(--blue)' : 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {activeTenantName || 'Select tenant…'}
                </span>
              </div>
              <ChevronDown size={12} style={{ color: 'var(--text-dim)', flexShrink: 0, transform: switcherOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>

            {switcherOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 12, right: 12, zIndex: 200, background: 'var(--surface)', border: '1px solid var(--navy-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', overflow: 'hidden', marginTop: 4 }}>
                <button onClick={handleClear} style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: !activeTenantId ? 'rgba(37,99,235,0.1)' : 'none', border: 'none', borderBottom: '1px solid var(--navy-border)', cursor: 'pointer', fontSize: 12, color: !activeTenantId ? 'var(--blue)' : 'var(--text-mid)' }}>
                  Global Admin view
                </button>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {tenants.map(t => (
                    <button key={t.id} onClick={() => handleSwitch(t)} style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: activeTenantId === t.id ? 'rgba(37,99,235,0.1)' : 'none', border: 'none', borderBottom: '1px solid var(--navy-border)', cursor: 'pointer', fontSize: 12, color: activeTenantId === t.id ? 'var(--blue)' : 'var(--text)' }}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <nav className="sidebar-nav">
          <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <LayoutDashboard size={16} /> Dashboard
          </NavLink>
          <NavLink to="/jobs" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <History size={16} /> Job History
          </NavLink>
          <NavLink to="/connection" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <Settings2 size={16} /> Connection Settings
          </NavLink>
          {isAdmin && (
            <NavLink to="/admin/tenants" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              <Users size={16} /> Tenants
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/admin/administrators" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              <UserCog size={16} /> Administrators
            </NavLink>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-badge">
            <div className="user-avatar">{initials}</div>
            <div className="user-info" onClick={() => setShowPasswordModal(true)}
                 style={{ cursor: 'pointer' }} title="Account settings — change password">
              <div className="user-name">{user?.full_name || user?.email}</div>
              <div className="user-role">{isAdmin ? 'Global Admin' : 'Tenant User'}</div>
            </div>
            <button className="logout-btn" onClick={() => setShowPasswordModal(true)}
                    title="Change password" style={{ marginRight: 4 }}>
              <Lock size={15} />
            </button>
            <button className="logout-btn" onClick={handleLogout} title="Sign out">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Viewing-as banner */}
        {isAdmin && activeTenantId && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 28px', background: 'rgba(37,99,235,0.12)', borderBottom: '1px solid rgba(37,99,235,0.25)', fontSize: 12, color: 'var(--blue)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Building2 size={13} />
              <span>Viewing as tenant: <strong>{activeTenantName}</strong></span>
            </div>
            <button onClick={handleClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <X size={13} /> Exit tenant view
            </button>
          </div>
        )}
        <main className="main-content fade-in">
          <Outlet />
        </main>
      </div>

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
}
